import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useCart } from "@/context/CartContext";
import { useLanguage } from "@/context/LanguageContext";
import { scrollToSection } from "@/lib/scroll";
import LanguagePopover from "@/components/sections/LanguagePopover";
import LanguageSwitcher from "@/components/sections/LanguageSwitcher";

/**
 * Navbar — Minimal luxury header (Rolex/Cartier-style).
 *
 * Layout (mobile-first):
 *   LEFT   : hamburger                     h-11 w-11
 *   CENTER : Kelo Diamonds gold monogram   absolute-centered, 38px wide
 *   RIGHT  : language selector + cart      h-10 w-10 each
 *
 * • White background, 60px tall (56–64 range).
 * • No "KELO DIAMONDS" text — the diamond IS the wordmark.
 * • Cart is a clean icon; a discreet numeric badge only when count > 0.
 *   No pulsing, no halo ring, no floating dots.
 * • Sticky top; on scroll the header condenses to 52px and gets a hairline
 *   bottom border for depth without covering content.
 */
export default function Navbar() {
  const { count, setOpen } = useCart();
  const { t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [menu, setMenu] = useState(false);
  const menuBtnRef = useRef(null);

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        setScrolled(window.scrollY > 12);
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!menu) {
      document.body.removeAttribute("data-mobile-menu-open");
      window.dispatchEvent(new CustomEvent("kelo:menu", { detail: { open: false } }));
      return;
    }
    document.body.setAttribute("data-mobile-menu-open", "1");
    window.dispatchEvent(new CustomEvent("kelo:menu", { detail: { open: true } }));
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") setMenu(false); };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
      menuBtnRef.current?.focus({ preventScroll: true });
    };
  }, [menu]);

  const links = [
    { label: t.nav.shop, href: "#flagship" },
    { label: t.nav.bestSellers, href: "#collection" },
    { label: t.nav.sizeGuide, href: "#size-guide" },
    { label: t.nav.whyMoissanite, href: "#why-moissanite" },
    { label: t.nav.ourStory, href: "#our-story" },
    { label: t.nav.faq, href: "#faq" },
  ];

  const go = (href) => {
    const wasOpen = menu;
    setMenu(false);
    window.setTimeout(() => scrollToSection(href), wasOpen ? 320 : 0);
  };

  const goHome = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <header
      data-testid="navbar"
      data-scrolled={scrolled ? "1" : "0"}
      className={`sticky top-0 z-50 border-b bg-white transition-[height,border-color] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${
        scrolled ? "border-[#eee]" : "border-transparent"
      }`}
      style={{ height: scrolled ? "64px" : "70px" }}
    >
      <nav className="relative mx-auto flex h-full max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-10">
        {/* ── LEFT: hamburger (mobile) + desktop nav links ─────────────── */}
        <div className="flex items-center gap-6">
          <button
            ref={menuBtnRef}
            data-testid="navbar-menu-toggle"
            onClick={() => setMenu((v) => !v)}
            aria-expanded={menu}
            aria-controls="mobile-menu"
            aria-label={t.nav.menu}
            data-open={menu}
            className="burger press flex h-11 w-11 flex-col items-center justify-center gap-[5px] rounded-full text-[#111] lg:hidden"
          >
            <span className="burger-line" />
            <span className="burger-line" />
            <span className="burger-line" />
          </button>

          <div className="hidden items-center gap-6 lg:flex">
            {links.slice(0, 3).map((l) => (
              <button
                key={l.href}
                data-testid={`nav-${l.href.replace("#", "")}`}
                onClick={() => go(l.href)}
                className="nav-underline font-body text-[13px] uppercase tracking-[0.18em] text-[#333] transition-colors hover:text-[#111]"
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── CENTER: gold diamond monogram (absolute for exact center) ── */}
        <button
          type="button"
          data-testid="navbar-logo"
          onClick={goHome}
          aria-label="Kelo Diamonds — Home"
          className="press pointer-events-auto absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 focus:outline-none"
        >
          <img
            src="/images/kelo-diamond-logo-nav.png"
            alt="Kelo Diamonds"
            width={80}
            height={70}
            decoding="async"
            fetchPriority="high"
            className="block h-[52px] w-auto sm:h-[58px]"
            style={{ filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.08))" }}
          />
        </button>

        {/* ── RIGHT: language + cart ────────────────────────────────── */}
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Desktop remaining links to keep them accessible */}
          <div className="hidden items-center gap-6 lg:mr-2 lg:flex">
            {links.slice(3).map((l) => (
              <button
                key={l.href}
                data-testid={`nav-${l.href.replace("#", "")}`}
                onClick={() => go(l.href)}
                className="nav-underline font-body text-[13px] uppercase tracking-[0.18em] text-[#333] transition-colors hover:text-[#111]"
              >
                {l.label}
              </button>
            ))}
          </div>

          <LanguagePopover />

          <button
            data-testid="navbar-cart-btn"
            onClick={() => setOpen(true)}
            aria-label={t.nav.cart}
            className="press relative flex h-10 w-10 items-center justify-center text-[#111] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#111]/25"
          >
            {/* Minimal outline shopping cart — no fill, no background */}
            <svg
              viewBox="0 0 24 24"
              width="22"
              height="22"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M3 4h2l2.4 12.2a2 2 0 0 0 2 1.6h8.2a2 2 0 0 0 2-1.5L21.5 8H6.2" />
              <circle cx="10" cy="20.5" r="1.15" />
              <circle cx="17.5" cy="20.5" r="1.15" />
            </svg>
            {count > 0 && (
              <span
                key={count}
                data-testid="navbar-cart-count"
                className="absolute -right-1 -top-0.5 flex h-[15px] min-w-[15px] items-center justify-center rounded-full bg-[#111] px-1 font-body text-[9px] font-medium leading-none text-white"
              >
                {count}
              </span>
            )}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {menu && (
          <>
            <motion.div
              data-testid="menu-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.22 }}
              onClick={() => setMenu(false)}
              className="fixed inset-0 z-40 bg-black/25 lg:hidden"
            />
            <motion.div
              id="mobile-menu"
              data-testid="mobile-menu"
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="absolute left-0 right-0 top-full z-50 max-h-[calc(100svh-64px)] overflow-y-auto border-t border-[#eee] bg-white lg:hidden"
              style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
            >
              <div className="flex flex-col px-5 py-2">
                <div className="border-b border-[#f0f0f0] py-3">
                  <LanguageSwitcher className="text-[#111]" />
                </div>
                {links.map((l, i) => (
                  <motion.button
                    key={l.href}
                    data-testid={`mobile-nav-${l.href.replace("#", "")}`}
                    onClick={() => go(l.href)}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.24, delay: 0.05 + i * 0.04, ease: [0.16, 1, 0.3, 1] }}
                    className="press min-h-[48px] border-b border-[#f0f0f0] py-3.5 text-left font-body text-base text-[#222] active:bg-[#f7f7f5]"
                  >
                    {l.label}
                  </motion.button>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  );
}
